/**
 * DIE KAUFMAENNISCHE SICHT IN DER BESTELLAKTE.
 *
 * Bestellwert, was vereinbart wurde, was tatsaechlich gezahlt wurde, was
 * daraus folgt. Keine Buchhaltungssoftware -- die Tatsachen dieser
 * Bestellung, in der Reihenfolge, in der man sie liest.
 *
 * DREI TRENNUNGEN STEHEN AUCH AUF DEM BILDSCHIRM
 *
 *   Die VEREINBARUNG steht ueber den Zahlungen und sieht anders aus:
 *   sie ist ein Satz, keine Zeile in einer Liste. Wer sie fuer einen
 *   Zahlungsnachweis haelt, haelt „30 % Anzahlung" fuer geflossenes
 *   Geld.
 *
 *   BEZAHLT und OFFEN sind GERECHNET. Es gibt kein Feld, in das jemand
 *   einen abweichenden Restbetrag schreiben koennte.
 *
 *   ERFUELLT steht hier gar nicht. Ob die Ware da ist, sagen die
 *   Termine -- und aus „vollstaendig bezahlt" folgt das nicht.
 */
import { useCallback, useEffect, useState } from "react";
import type { PaymentKind, PaymentMethod, PurchaseOrderFinance,
  PurchaseOrderPayment } from "@homebase/shared";
import { PAYMENT_KINDS, PAYMENT_METHODS } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { errorText } from "../i18n/errors.ts";
import type { I18n } from "../i18n/index.tsx";
import { zahlplanSatz } from "./SupplierBits.tsx";

/** Ein Betrag in der Sprache des Benutzers. Immer zwei Nachkommastellen. */
function betrag(roh: string | null, waehrung: string | null, lang: string): string {
  if (roh === null) return "—";
  const n = Number(roh);
  if (!Number.isFinite(n)) return roh;
  return `${n.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    + `${waehrung === null ? "" : ` ${waehrung}`}`;
}

const kurzDatum = (iso: string, lang: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(lang, {
    day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC",
  });

interface Entwurf {
  datum: string; betrag: string; art: PaymentKind; notiz: string; referenz: string;
  /** Der tatsaechlich gegangene Weg (0094). Leer heisst: nicht erfasst. */
  weg: string;
  /** Die erfuellte Stufe (0094). Leer heisst: keiner zugeordnet. */
  stufe: string;
}
/* LEER HEISST LEER -- bei beiden neuen Feldern. „Ueberweisung"
   vorzuwaehlen hiesse, den Normalfall zu unterstellen, und die erste
   offene Stufe vorzuwaehlen hiesse, eine Zuordnung zu raten. */
const leer = (): Entwurf =>
  ({ datum: "", betrag: "", art: "deposit", notiz: "", referenz: "", weg: "", stufe: "" });

export function OrderFinance({ poId, stand, i18n, mayWrite, onChanged }: {
  poId: number;
  /**
   * Der Stand der Akte -- sie erhoeht ihn bei jedem Nachladen.
   *
   * DER BEFUND AUS DER PRAXIS (§13). Dieser Bereich holt seinen
   * Bestellwert selbst und wusste deshalb nichts von einer neuen
   * POSITION. Er hing an der Zahl der Zahlungen, und die aendert eine
   * Position nicht: die Akte zeigte oben 45.508,64 und hier
   * 43.208,64.
   *
   * Es ist bewusst ein Zaehler und kein Betrag: was sich geaendert
   * hat, muss dieser Bereich nicht wissen -- nur DASS.
   */
  stand: number;
  i18n: I18n; mayWrite: boolean;
  /** Der Bestellwert kann sich geaendert haben -- die Akte laedt dann neu. */
  onChanged: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [d, setD] = useState<PurchaseOrderFinance | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  /* Welche Zahlung gerade ihren Nachweis bekommt -- `null` ist keine. */
  const [laeuft, setLaeuft] = useState<number | null>(null);

  const laden = useCallback(async () => {
    try { setD(await api.purchaseOrderFinance(poId)); }
    catch (e) { setFehler(e); }
  }, [poId, stand]);
  useEffect(() => { void laden(); }, [laden]);

  if (d === null) {
    return <section className="log__group" data-testid="plp-finanzen">
      <h4 className="log__grouptitle">{t("plp.finance")}</h4>
      <p className="work__empty">{t("common.loading")}</p>
    </section>;
  }

  async function erfassen(): Promise<void> {
    if (entwurf === null) return;
    setSpeichert(true);
    try {
      setD(await api.addPayment(poId, {
        paidOn: entwurf.datum,
        amount: entwurf.betrag.trim().replace(",", "."),
        kind: entwurf.art,
        note: entwurf.notiz.trim() === "" ? null : entwurf.notiz.trim(),
        externalReference: entwurf.referenz.trim() === "" ? null : entwurf.referenz.trim(),
        paymentMethod: entwurf.weg === "" ? null : (entwurf.weg as PaymentMethod),
        paymentTermStageId: entwurf.stufe === "" ? null : Number(entwurf.stufe),
      }));
      setEntwurf(null); setFehler(null); onChanged();
    } catch (e) { setFehler(e); } finally { setSpeichert(false); }
  }
  /**
   * Einen Zahlungsnachweis an DIESE Zahlung haengen.
   *
   * Dieselbe Ablage wie bei PO, PI und Packing List -- dieselben
   * Schranken, dieselbe Pruefsumme, dieselbe Abloesung. Was hier
   * NICHT passiert: ein Betrag aendern oder eine Zahlung auf
   * „bezahlt" setzen. Ein Beleg ist Evidenz, keine Buchung.
   */
  async function nachweisAblegen(z: PurchaseOrderPayment, datei: File): Promise<void> {
    setLaeuft(z.id); setFehler(null);
    try {
      const roh = new Uint8Array(await datei.arrayBuffer());
      let binaer = "";
      for (const b of roh) binaer += String.fromCharCode(b);
      await api.uploadPaymentEvidence(poId, z.id, {
        fileName: datei.name, contentBase64: btoa(binaer),
        replacesId: z.documentId,
      });
      await laden();
      onChanged?.();
    } catch (e) { setFehler(e); } finally { setLaeuft(null); }
  }

  /**
   * Eine Zahlung ihrer Stufe zuordnen -- oder die Zuordnung loesen.
   *
   * GESCHICKT WIRD DIE GANZE ZAHLUNG. Der Weg erwartet das seit 0074,
   * und nur die Stufe zu schicken hiesse, Datum, Betrag, Waehrung, Art,
   * Notiz und Fremdbeleg zu leeren -- jene Felder werden ERSETZT, nicht
   * ergaenzt.
   *
   * `paymentMethod` geht bewusst NICHT mit: es gehoert zu den zwei
   * Feldern, die der Server nur anfasst, wenn sie genannt werden. Wer
   * hier eine Stufe zuordnet, will den erfassten Zahlungsweg nicht
   * aendern -- und ihn mitzuschicken hiesse, ihn bei jeder Zuordnung
   * neu zu behaupten.
   */
  async function zuordnen(z: PurchaseOrderPayment, stufe: number | null): Promise<void> {
    setSpeichert(true);
    try {
      setD(await api.updatePayment(poId, z.id, {
        paidOn: z.paidOn, amount: z.amount, currency: z.currency, kind: z.kind,
        note: z.note, externalReference: z.externalReference,
        paymentTermStageId: stufe,
      }));
      setFehler(null);
    } catch (e) { setFehler(e); } finally { setSpeichert(false); }
  }

  async function entfernen(z: PurchaseOrderPayment): Promise<void> {
    setSpeichert(true);
    try { setD(await api.deletePayment(poId, z.id)); setFehler(null); onChanged(); }
    catch (e) { setFehler(e); } finally { setSpeichert(false); }
  }

  return (
    <section className="log__group plp__finanzen" data-testid="plp-finanzen">
      <h4 className="log__grouptitle">{t("plp.finance")}</h4>

      {fehler !== null && (
        <p className="log__error" role="alert" data-testid="plp-finanzfehler">
          {errorText(i18n, fehler)}
        </p>
      )}

      {/*
        * ── A · DER OBERE KOMPAKTBLOCK (§2A) ────────────────────────
        *
        * Eine Zeile Schluessel/Wert und keine Tabelle. Sie trug
        * `log__rows`, das Raster der Logistikkarten -- hier steht
        * aber genau EINE Angabe, und sie bekommt deshalb ihren
        * eigenen, engen Namen. Dadurch hat dieser Block dieselbe
        * linke Innenkante wie die Sektionstitel darunter; vorher kam
        * sie aus einer fremden Datei und stimmte nur zufaellig.
        */}
      <div className="plp__finkopf">
        {/*
          * DER BESTELLWERT -- UND OB ER VOLLSTAENDIG IST.
          *
          * DER BEFUND AUS DER PRAXIS (§14). Die Summe addierte die
          * bepreisten Positionen und sah fertig aus. Positionen ohne
          * Preis fielen still heraus; wer die Zahl las, hatte keinen
          * Anlass nachzusehen.
          *
          * Die Positionskarte sagte es bereits -- dieser Bereich
          * nicht, und er ist der, in dem jemand ueber Geld
          * entscheidet. Die Angabe steht jetzt an BEIDEN Stellen und
          * kommt aus derselben Zaehlung.
          */}
        <div className="plp__finzeile">
          <span className="plp__finlabel">{t("plp.orderValue")}</span>
          <span className="plp__finwert" data-testid="plp-finwert"
            data-vollstaendig={d.linesWithoutPrice === 0 ? "ja" : "nein"}>
            {d.orderValue === null ? t("plp.noValue") : betrag(d.orderValue, d.currency, i18n.lang)}
          </span>
        </div>
        {d.linesWithoutPrice > 0 && d.orderValue !== null && (
          <p className="plp__finhinweis" data-testid="plp-finwertunvollstaendig">
            {t("plp.valuePartial", { n: String(d.linesWithoutPrice) })}
          </p>
        )}
        {/*
          * ── DIE VEREINBARUNG STAND HIER ZWEIMAL (§10) ──────────────
          *
          * DER BEFUND DER BROWSER-ABNAHME AN PO79707. Oben stand der
          * Plan als SATZ („35 % Anzahlung · 65 % bei Ankunft"),
          * darunter die Herkunft, und danach noch einmal derselbe Plan
          * als TABELLE. Dreimal dieselbe Abrede in drei Formen -- das
          * war der zerrissene Eindruck, und er kam nicht von
          * Abstaenden, sondern von der Wiederholung.
          *
          * DER SATZ IST GEGANGEN, DIE TABELLE GEBLIEBEN. Sie sagt
          * mehr: Anteil UND Sollbetrag UND was darauf bezahlt ist. Ein
          * Satz daneben kann davon nichts, und er nimmt der Tabelle
          * den Platz.
          *
          * DER SATZ LEBT WEITER, wo er der richtige ist: am
          * Lieferanten und an der Artikelbeziehung gibt es keinen
          * Bestellwert, also auch kein Soll -- dort IST der Plan ein
          * Satz. Siehe `zahlplanSatz`.
          */}
      </div>

      {/*
        * ── DER PLAN, AUF DIESE BESTELLUNG GERECHNET (0094 · §9) ───────
        *
        * ER STEHT ZWISCHEN DER VEREINBARUNG UND DEN ZAHLUNGEN, weil er
        * genau das ist: die Abrede von oben, angewendet auf den
        * Bestellwert und die Zahlungen von unten.
        *
        * ALLES GERECHNET, NICHTS GEPFLEGT. Soll, bezahlt und Rest stehen
        * nirgends in der Datenbank -- gespeichert waeren sie die
        * Wahrheit, die als erste veraltet.
        *
        * OHNE PLAN GIBT ES DIE KARTE NICHT. Eine leere Tabelle mit drei
        * Spaltenkoepfen sagt nichts, was der Satz oben nicht schon sagt.
        */}
      {/*
        * OHNE PLAN EIN SATZ, MIT PLAN EINE TABELLE. Beides ist eine
        * Auskunft; ein leerer Tabellenkopf waere keine.
        */}
      {d.terms === null ? (
        <p className="log__hint plp__planleer" data-testid="plp-finplan">
          {t("plp.noPaymentTerms")}
        </p>
      ) : (
        <div className="plp__sektion" data-testid="plp-finplan">
          {/*
            * ── DIE HIERARCHIE WIRD AUSGESPROCHEN (§10) ──────────────
            *
            * „1. Zahlungsvereinbarung / Plan, 2. Tatsaechliche
            * Zahlungen" -- und zwar als Ueberschrift, nicht als
            * Reihenfolge, die man erraten muss.
            *
            * MEIN ERSTER ENTWURF HATTE SIE VERGESSEN: beim Zusammen-
            * legen von Satz und Tabelle fiel die Tabellenbeschriftung
            * mit weg, und die Karte begann ohne Namen. Gemerkt hat es
            * `checki18n` C1 -- zwei Schluessel wurden unbenutzt, und
            * einer davon war genau diese Ueberschrift.
            */}
          <div className="plp__sektionkopf">
            <div className="plp__sektionnamen">
              <h5 className="plp__sektiontitel">{t("plp.paymentTerms")}</h5>
          {/*
            * DIE HERKUNFT STEHT OBEN, NICHT UNTEN (§10).
            *
            * Sie wirkte „nachtraeglich daruntergesetzt", weil sie es
            * war. Wer eine Abrede liest, will ZUERST wissen, wessen
            * Abrede es ist -- danach, wie sie aussieht. Ruhig
            * gesetzt: eine Herkunft ist kein Befund.
            */}
              <p className="plp__sektionquelle">
                {t(d.terms.level === "purchase_order"
                  ? (d.terms.method === "uebernommen"
                      ? "plp.termsTakenOver" : "plp.termsFromOrder")
                  : d.terms.level === "article"
                    ? "plp.termsFromArticle" : "plp.termsFromSupplier")}
              </p>
            </div>
            {/*
              * ── DIE HANDLUNG STEHT RECHTS IN DER KOPFZEILE (§2B) ──
              *
              * UND SIE IST EINE AUSKUNFT UND KEIN KNOPF, weil es den
              * Knopf nicht gibt: fuer die Zahlungsvereinbarung EINER
              * BESTELLUNG hat HOMEBASE heute keinen Bearbeitungsweg.
              * Die Serverroute kennt `purchase-orders/:id/
              * payment-terms`, der Client bietet sie nur fuer
              * `suppliers` und `supplier-articles` an -- geprueft an
              * `api.setPaymentTerms`.
              *
              * Einen Knopf hinzuschreiben, der nichts oeffnet, waere
              * genau die Sackgasse, die §20 des Vorblocks abgestellt
              * hat. Die Zeile sagt stattdessen, WO die Vereinbarung
              * gepflegt wird -- das ist der Unterschied zwischen
              * „nicht hier" und „nirgends".
              */}
            <p className="plp__sektionaktion" data-testid="plp-termswo">
              {t("plp.termsWhereEdited")}
            </p>
          </div>
          {/*
            * ── OHNE `sup__table` (§6/§10) ─────────────────────────
            *
            * Die Tabelle trug die Partnertabellenklasse mit, und die
            * bringt drei Dinge mit, die hier falsch sind:
            *
            *   thead th { position: sticky; top: 0 }
            *         Die Partnertabelle hat ihren EIGENEN
            *         Bildlaufkasten -- dort klebt der Kopf darin.
            *         In der Bestellakte scrollt das FENSTER, also
            *         klebte der Kopf am Fensterrand und schob sich
            *         UEBER die naechste Tabelle. Das ist das
            *         „ineinander geschoben" der Abnahme.
            *   tbody tr:nth-child(even)
            *         Zebrastreifen helfen beim Abtasten langer
            *         Listen. Ein Zahlungsplan hat zwei bis vier
            *         Zeilen.
            *   tbody tr:hover td
            *         Behauptet Interaktion, wo keine ist (§8).
            *
            * `plp__fintabelle` ist die gemeinsame Basis BEIDER
            * Finanztabellen -- Plan und Ist sehen gleich aus, weil
            * sie dasselbe Muster sind.
            */}
          <table className="plp__fintabelle plp__plantabelle" data-testid="plp-stufen">
            {/*
              * SIEBEN SPALTEN, und der Anteil hat seine EIGENE (§10).
              * Er stand als Unterzeile in der Bezeichnung und war
              * damit nicht vergleichbar -- „35 %" und „65 %"
              * untereinander lesen heisst, sie in einer Spalte zu
              * haben.
              */}
            <thead><tr>
              <th className="plp__spbez">{t("ek.stageLabel")}</th>
              <th className="sup__num plp__spanteil">{t("fin.stageShare")}</th>
              <th>{t("ek.stageMethod")}</th>
              <th className="sup__num">{t("fin.stageDue")}</th>
              <th className="sup__num">{t("fin.stagePaid")}</th>
              <th className="sup__num">{t("fin.stageRest")}</th>
              <th className="plp__spstatus">{t("fin.stageStatus")}</th>
            </tr></thead>
            <tbody>
              {d.stages.map((st) => (
                <tr key={st.id} data-testid="plp-stufe" data-stufe={st.id}
                  data-erfuellt={String(st.settled)}>
                  <td className="plp__spbez">
                    {/* DIE BESCHRIFTUNG, WENN ES EINE GIBT -- sonst das
                        Ereignis. „—" waere hier eine Luecke, wo keine
                        ist: eine Stufe ohne Namen heisst nach ihrem
                        Ereignis. */}
                    {st.label ?? i18n.tCode("payEvent", st.event)}
                    {/*
                      * DIE UNTERZEILE TRAEGT, WAS SONST VERLOREN GEHT.
                      *
                      * Das Ereignis (nur wenn die Beschriftung es
                      * nicht schon ist), die Frist und den ZUSATZ --
                      * „vor B/L", „Lueneburg". Der Zusatz stand vorher
                      * nur im Satz, und als der Satz ging, ging er
                      * mit: gemessen an FN-U3a, das „before B/L"
                      * nicht mehr fand. Eine Angabe, die ein Mensch
                      * eingetragen hat, verschwindet nicht, weil die
                      * Darstellung wechselt.
                      */}
                    {(() => {
                      const teile: string[] = [];
                      if (st.label !== null) teile.push(i18n.tCode("payEvent", st.event));
                      if (st.offsetDays !== null) {
                        teile.push(t("ek.termsAfterDays", { n: st.offsetDays }));
                      }
                      if (st.eventNote !== null && st.eventNote !== "") {
                        teile.push(st.eventNote);
                      }
                      return teile.length === 0 ? null
                        : <span className="plp__pobez">{teile.join(" · ")}</span>;
                    })()}
                  </td>
                  {/* DER ANTEIL IN EIGENER SPALTE, rechtsbuendig wie
                      jede Zahl -- so lassen sich 35 und 65 vergleichen,
                      ohne sie zu suchen. */}
                  <td className="sup__num plp__spanteil">
                    {Number(st.sharePercent).toLocaleString(i18n.lang,
                      { maximumFractionDigits: 2 })} %
                  </td>
                  <td>
                    {st.paymentMethod === null
                      ? <span className="log__hint">{t("ek.stageMethodNone")}</span>
                      : i18n.tCode("payMethod", st.paymentMethod)}
                  </td>
                  <td className="sup__num">
                    {/* OHNE BESTELLWERT KEIN SOLLBETRAG -- und der Grund
                        steht daneben, nicht ein Strich. */}
                    {st.dueAmount === null
                      ? <span className="log__hint" data-testid="plp-stufekeinsoll">
                          {t("fin.stageNoDue")}</span>
                      : betrag(st.dueAmount, d.currency, i18n.lang)}
                  </td>
                  <td className="sup__num">{betrag(st.paid, d.currency, i18n.lang)}</td>
                  <td className="sup__num">
                    {st.remaining === null
                      ? <span className="log__hint">—</span>
                      : betrag(st.remaining, d.currency, i18n.lang)}
                  </td>
                  <td className="plp__spstatus">
                    {/*
                      * ── DREI ZUSTAENDE, GERECHNET (§7A) ───────────
                      *
                      * Die Spalte hiess „erfuellt" und kannte zwei
                      * Werte. Sie beschreibt aber einen STATUS, und
                      * der hat drei Stufen:
                      *
                      *   offen       nichts darauf gezahlt
                      *   teilweise   etwas, aber nicht genug
                      *   erfuellt    mindestens der Sollbetrag
                      *
                      * KEINE NEUE FACHLOGIK. Alle drei folgen aus
                      * `paid` und `remaining`, die es seit 0094 gibt;
                      * „teilweise" war bisher nur nicht AUSGESPROCHEN
                      * und steckte in „offen" mit drin. Eine
                      * Teilzahlung von 200 auf 500 Soll ist etwas
                      * anderes als gar keine Zahlung.
                      *
                      * UND OHNE SOLLBETRAG KEINS VON DREIEN. Ohne
                      * Bestellwert ist die Stufe unbeurteilbar;
                      * „offen" waere eine Aussage, die niemand
                      * pruefen kann.
                      */}
                    {st.remaining === null
                      ? <span className="log__hint">{t("fin.stageUnknown")}</span>
                      : st.settled
                        ? <span className="plp__status plp__status--erfuellt">
                            {t("fin.stageSettled")}</span>
                        : Number(st.paid) > 0
                          ? <span className="plp__status plp__status--teilweise">
                              {t("fin.stagePartly")}</span>
                          : <span className="plp__status plp__status--offen">
                              {t("fin.stageOpen")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/*
            * ZAHLUNGEN OHNE STUFE -- genannt, nicht geraten.
            *
            * Der bequeme Fehler waere, sie der ersten offenen Stufe
            * zuzuschlagen: bei 30/70 sieht die Vermutung richtig aus und
            * ist bei einer Teilzahlung auf die Restzahlung falsch.
            */}
          {d.unassignedPayments.length > 0 && (
            <p className="log__hint plp__ohnestufe" data-testid="plp-ohnestufe">
              {t("fin.unassigned")}: {d.unassignedPayments.length} — {t("fin.unassignedHint")}
            </p>
          )}
        </div>
      )}

      {/*
        * ── TATSAECHLICHE ZAHLUNGEN -- KLAR GETRENNT (§10) ────────────
        *
        * Was gezahlt werden SOLLTE, steht oben; was GEFLOSSEN ist,
        * steht hier. Die Ueberschrift sagt es, damit niemand die
        * beiden Tabellen fuer eine haelt.
        */}
      {/*
        * ── ZWEITE SEKTION: WAS WIRKLICH GEFLOSSEN IST (§7B) ──────────
        *
        * SIE IST EINE SEKTION WIE DIE ERSTE -- derselbe Titelstil,
        * dieselbe Tabellenbasis, dieselbe Trennlinie darueber. Vorher
        * stand die Ueberschrift frei auf der Flaeche und die Tabelle in
        * einem fremden Rahmen (`sup__tablewrap`); dadurch gehoerte
        * sichtbar nichts zusammen.
        *
        * UND `+ Zahlung erfassen` STEHT JETZT DARIN. Der Knopf lag
        * hinter den Befunden, also hinter einem Block, der den GANZEN
        * Finanzbereich betrifft -- und sah damit aus, als gehoere er
        * zum Bereich und nicht zu dieser Tabelle.
        */}
      <div className="plp__sektion">
        {/*
          * ── C · DIE HANDLUNG GEHOERT IN DIE KOPFZEILE (§2C) ───────
          *
          * `+ Zahlung erfassen` stand UNTER der Tabelle, also am Ende
          * eines Blocks, dessen Ueberschrift man dann nicht mehr
          * sieht. Jetzt steht sie rechts neben „Tatsaechliche
          * Zahlungen" -- dort, wo man sie sucht, wenn man die
          * Ueberschrift gelesen hat.
          */}
        <div className="plp__sektionkopf">
          <div className="plp__sektionnamen">
            <h5 className="plp__sektiontitel">{t("fin.stages")}</h5>
          </div>
          {mayWrite && entwurf === null && (
            <span className="plp__sektionaktion">
              <button className="btn btn--small" data-testid="plp-zahlungneu"
                onClick={() => setEntwurf(leer())}>{t("plp.addPayment")}</button>
            </span>
          )}
        </div>
        {/*
          * ── KEINE LEERE TABELLENHUELLE (§2C) ─────────────────────
          *
          * Gemessen an PO79707: ohne eine einzige Zahlung stand hier
          * ein vollstaendiger Kopf aus ACHT Spalten und darunter eine
          * Zeile mit `colSpan`. Acht Spaltennamen sind eine Zusage auf
          * Inhalt, den es nicht gibt -- und der Kopf allein trug zwei
          * der waagerechten Linien, die die Abnahme bemerkt hat.
          *
          * Der Satz sagt dasselbe in einer Zeile. Die Tabelle
          * erscheint, sobald es eine Zahlung gibt.
          */}
        {d.payments.length === 0 ? (
          <p className="plp__leer" data-testid="plp-keinezahlung">
            {t("plp.noPayments")}
          </p>
        ) : (
        <table className="plp__fintabelle" data-testid="plp-zahlungen">
          <thead><tr>
            <th>{t("plp.paidOn")}</th>
            <th>{t("plp.paymentKind")}</th>
            <th className="sup__num">{t("plp.paymentAmount")}</th>
            {/* Die Stufe nur, wenn es ueberhaupt einen Plan gibt. Eine
                Spalte, in der jede Zelle „—" sagt, ist keine Auskunft. */}
            {d.stages.length > 0 && <th>{t("fin.stageAssign")}</th>}
            <th>{t("ek.stageMethod")}</th>
            <th>{t("plp.paymentNote")}</th>
            <th>{t("pay.evidence")}</th>
            <th />
          </tr></thead>
          <tbody>
            {d.payments.map((z) => (
              <tr key={z.id} data-testid="plp-zahlung" data-art={z.kind}>
                <td>{kurzDatum(z.paidOn, i18n.lang)}</td>
                <td>{i18n.tCode("payKind", z.kind)}</td>
                <td className="sup__num">
                  {betrag(z.amount, z.currency, i18n.lang)}
                  {/* Eine Zahlung in anderer Waehrung faellt auf, statt
                      still in die Summe zu rutschen. */}
                  {d.currency !== null && z.currency !== d.currency && (
                    <span className="plp__befund" data-befund="fremdwaehrung">
                      {t("plp.foreignCurrency")}
                    </span>
                  )}
                </td>
                {/*
                  * ── DIE STUFE, DIE DIESE ZAHLUNG ERFUELLT (§11) ──
                  *
                  * GEWAEHLT WIRD AUS DEM GELTENDEN PLAN. Eine Stufe
                  * aus einem anderen Plan weist der Server ab -- sie
                  * hier nicht anzubieten ist dasselbe eine Stufe
                  * frueher, statt den Fehler erst beim Speichern zu
                  * zeigen.
                  *
                  * DIE LEERE AUSWAHL BLEIBT ERREICHBAR. Eine falsch
                  * gesetzte Zuordnung muss sich loesen lassen, und
                  * „nicht zugeordnet" ist ein Zustand und kein Fehler.
                  */}
                {d.stages.length > 0 && (
                  <td data-testid="plp-zahlungstufe"
                    data-stufe={z.paymentTermStageId === null ? "" : String(z.paymentTermStageId)}>
                    {mayWrite ? (
                      <select className="log__input" disabled={speichert}
                        aria-label={t("fin.stageAssign")}
                        value={z.paymentTermStageId === null ? "" : String(z.paymentTermStageId)}
                        onChange={(e) => void zuordnen(z,
                          e.target.value === "" ? null : Number(e.target.value))}>
                        <option value="">{t("fin.stageNone")}</option>
                        {d.stages.map((st) => (
                          <option key={st.id} value={String(st.id)}>
                            {st.label ?? i18n.tCode("payEvent", st.event)}
                          </option>
                        ))}
                      </select>
                    ) : z.paymentTermStageId === null
                      ? <span className="log__hint">{t("fin.stageNone")}</span>
                      : (() => {
                          const st = d.stages.find((x) => x.id === z.paymentTermStageId);
                          return st === undefined ? <span className="log__hint">—</span>
                            : <>{st.label ?? i18n.tCode("payEvent", st.event)}</>;
                        })()}
                  </td>
                )}
                {/*
                  * ── DER TATSAECHLICHE WEG (§10) ───────────────────
                  *
                  * Er darf vom vereinbarten abweichen, und wenn er es
                  * tut, STEHT DAS DA. Vereinbart war Akkreditiv,
                  * geflossen ist eine Ueberweisung, weil die Bank
                  * nicht mitspielte -- beides ist wahr, und die
                  * Abweichung ist kein Fehler, sondern das, was
                  * passiert ist.
                  */}
                <td data-testid="plp-zahlungmethode"
                  data-weg={z.paymentMethod ?? ""}>
                  {z.paymentMethod === null
                    ? <span className="log__hint">{t("payMethod.none")}</span>
                    : <>
                        {i18n.tCode("payMethod", z.paymentMethod)}
                        {(() => {
                          const st = d.stages.find((x) => x.id === z.paymentTermStageId);
                          return st?.paymentMethod !== undefined
                            && st?.paymentMethod !== null
                            && st.paymentMethod !== z.paymentMethod
                            ? <span className="plp__befund" data-befund="wegabweichung">
                                {t("fin.methodDiffers")}</span>
                            : null;
                        })()}
                      </>}
                </td>
                <td>
                  {z.note ?? <span className="log__hint">—</span>}
                  {/* Erfasst am ist nicht gezahlt am -- beides steht da. */}
                  <span className="plp__pobez">
                    {t("plp.recordedOn", {
                      wer: z.recordedBy ?? "—", wann: z.recordedAt.slice(0, 10),
                    })}
                  </span>
                </td>
                {/*
                  * ── DER NACHWEIS GEHOERT AN DIE ZAHLUNG ──────────
                  *
                  * Nicht an die Bestellung: spaeter muss
                  * nachvollziehbar sein, welcher Bankbeleg zu
                  * WELCHER Ueberweisung gehoert -- PO, PI, Zahlung,
                  * Nachweis. Eine Anzahlung und eine Restzahlung
                  * haben jede ihren eigenen.
                  *
                  * DAS ABLEGEN AENDERT KEINEN BETRAG und setzt keine
                  * Zahlung auf „bezahlt". Ein Beleg ist Evidenz,
                  * keine Buchung.
                  */}
                <td data-testid="plp-zahlungnachweis"
                  data-stand={z.documentId === null ? "fehlt" : "da"}>
                  {z.documentId === null ? (
                    <span className="plp__nachweisfehlt">
                      <span aria-hidden="true">{"\u26A0"}</span>{t("pay.evidenceMissing")}
                    </span>
                  ) : (
                    <span className="plp__nachweisda">
                      <span aria-hidden="true">{"\u2713"}</span>
                      <a data-testid="plp-nachweisoeffnen"
                        href={`/api/v1/documents/${z.documentId}/content?disposition=inline`}
                        target="_blank" rel="noreferrer">
                        {z.documentFileName ?? t("pay.evidence")}
                      </a>
                    </span>
                  )}
                  {mayWrite && (
                    <label className="btn btn--small plp__nachweisknopf">
                      {laeuft === z.id ? t("pod.uploading")
                        : t(z.documentId === null ? "pay.evidenceUpload" : "pay.evidenceNew")}
                      <input type="file" hidden accept="application/pdf"
                        data-testid={`plp-nachweisupload-${z.id}`} disabled={laeuft !== null}
                        aria-label={t(z.documentId === null
                          ? "pay.evidenceUpload" : "pay.evidenceNew")}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f !== undefined) void nachweisAblegen(z, f);
                        }} />
                    </label>
                  )}
                </td>
                <td>
                  {mayWrite && (
                    <button type="button" className="bew__weg" data-testid="plp-zahlungweg"
                      disabled={speichert} onClick={() => void entfernen(z)}>
                      {t("plp.removePayment")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}

        {/*
          * ── D · DIE ZUSAMMENFASSUNG (§2D) ────────────────────────
          *
          * SIE STAND IM `tfoot` DER ZAHLUNGSTABELLE, verteilt ueber
          * zwei Zeilen mit je drei `colSpan`-Zellen. Das war der
          * „lose" Eindruck der Abnahme, und er hatte einen Grund: die
          * beiden Zahlen gehoeren nicht in die Spalten der Zahlungen.
          * „Bezahlt gesamt" ist keine Zahlung, und es stand trotzdem
          * in der Betragsspalte der Zahlungen -- mit vier leeren
          * Zellen daneben.
          *
          * JETZT EIN EIGENER, KOMPAKTER BLOCK aus zwei Paaren,
          * rechtsbuendig untereinander. Er steht ausserhalb der
          * Tabelle und gilt deshalb auch dann, wenn es noch keine
          * Zahlung gibt -- und genau dann ist er die wichtigere
          * Auskunft: bezahlt 0,00, offen der ganze Bestellwert.
          *
          * BEIDE ZAHLEN BLEIBEN GERECHNET. `d.paid` und `d.open`
          * kommen unveraendert vom Server; hier wird nur anders
          * gesetzt, was dort schon stand.
          */}
        <dl className="plp__saldo" data-testid="plp-saldo">
          <div className="plp__saldozeile" data-testid="plp-bezahlt">
            <dt>{t("plp.paidTotal")}</dt>
            <dd>{betrag(d.paid, d.currency, i18n.lang)}</dd>
          </div>
          <div className="plp__saldozeile plp__saldozeile--offen" data-testid="plp-offen">
            <dt>{t("plp.stillOpen")}</dt>
            <dd>{d.open === null ? t("plp.noValue") : betrag(d.open, d.currency, i18n.lang)}</dd>
          </div>
        </dl>

      {mayWrite && entwurf !== null && ((
        <div className="sup__kform" data-testid="plp-zahlungmaske">
          <label className="sup__kfeld"><span className="sup__klabel">{t("plp.paidOn")}</span>
            <input className="log__input log__input--text" type="date" value={entwurf.datum}
              data-feld="paidOn" aria-label={t("plp.paidOn")}
              onChange={(e) => setEntwurf({ ...entwurf, datum: e.target.value })} />
            <span className="log__hint">{t("plp.paidOnHint")}</span></label>
          <label className="sup__kfeld"><span className="sup__klabel">{t("plp.paymentKind")}</span>
            <select className="log__input" value={entwurf.art} data-feld="kind"
              aria-label={t("plp.paymentKind")}
              onChange={(e) => setEntwurf({ ...entwurf, art: e.target.value as PaymentKind })}>
              {PAYMENT_KINDS.map((k) => (
                <option key={k} value={k}>{i18n.tCode("payKind", k)}</option>
              ))}
            </select></label>
          <label className="sup__kfeld">
            <span className="sup__klabel">
              {d.currency === null
                ? t("plp.paymentAmount") : t("plp.paymentAmountIn", { w: d.currency })}
            </span>
            <input className="log__input log__input--text" value={entwurf.betrag}
              data-feld="amount" aria-label={t("plp.paymentAmount")}
              onChange={(e) => setEntwurf({ ...entwurf, betrag: e.target.value })} /></label>
          <label className="sup__kfeld"><span className="sup__klabel">{t("ek.stageMethod")}</span>
            <select className="log__input" value={entwurf.weg} data-feld="paymentMethod"
              aria-label={t("ek.stageMethod")}
              onChange={(e) => setEntwurf({ ...entwurf, weg: e.target.value })}>
              <option value="">{t("payMethod.none")}</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{i18n.tCode("payMethod", m)}</option>
              ))}
            </select></label>
          {/* Die Stufe nur, wenn es einen Plan gibt -- sonst gibt es
              nichts zu waehlen, und ein leeres Feld fragt nach etwas,
              das es nicht gibt. */}
          {d.stages.length > 0 && (
            <label className="sup__kfeld">
              <span className="sup__klabel">{t("fin.stageAssign")}</span>
              <select className="log__input" value={entwurf.stufe} data-feld="paymentTermStageId"
                aria-label={t("fin.stageAssign")}
                onChange={(e) => setEntwurf({ ...entwurf, stufe: e.target.value })}>
                <option value="">{t("fin.stageNone")}</option>
                {d.stages.map((st) => (
                  <option key={st.id} value={String(st.id)}>
                    {st.label ?? i18n.tCode("payEvent", st.event)}
                  </option>
                ))}
              </select></label>
          )}
          <label className="sup__kfeld"><span className="sup__klabel">{t("plp.paymentReference")}</span>
            <input className="log__input log__input--text" value={entwurf.referenz}
              data-feld="externalReference" aria-label={t("plp.paymentReference")}
              placeholder={t("sup.optional")}
              onChange={(e) => setEntwurf({ ...entwurf, referenz: e.target.value })} /></label>
          <label className="sup__kfeld sup__kfeld--breit">
            <span className="sup__klabel">{t("plp.paymentNote")}</span>
            <input className="log__input log__input--text" value={entwurf.notiz}
              data-feld="note" aria-label={t("plp.paymentNote")}
              placeholder={t("sup.optional")}
              onChange={(e) => setEntwurf({ ...entwurf, notiz: e.target.value })} /></label>
          <span className="sup__kformaktionen">
            <button className="btn btn--primary btn--small" data-testid="plp-zahlungspeichern"
              disabled={speichert || entwurf.datum === "" || entwurf.betrag.trim() === ""}
              onClick={() => void erfassen()}>{t("log.save")}</button>
            <button className="btn btn--small" disabled={speichert}
              onClick={() => setEntwurf(null)}>{t("common.cancel")}</button>
          </span>
        </div>
      ))}
      </div>

      {/*
        * WAS NICHT AUFGEHT, STEHT DA -- und wird nicht still korrigiert.
        *
        * Keiner dieser Hinweise ist eine steuerliche Einordnung. Sie
        * sagen, wo die Zahlen einander widersprechen.
        *
        * SIE STEHEN HINTER BEIDEN SEKTIONEN, weil sie den ganzen
        * Finanzbereich betreffen -- nicht den Plan und nicht die
        * Zahlungen allein.
        */}
      {d.findings.length > 0 && (
        <p className="plp__befunde" data-testid="plp-finbefunde">
          {d.findings.map((f) => (
            <span key={f} className="plp__befund" data-befund={f}>
              {i18n.tCode("finBefund", f, f)}
            </span>
          ))}
        </p>
      )}
      {/* Zahlungs- und Erfuellungsstand sind getrennt -- und das steht da,
          damit niemand das eine aus dem anderen schliesst. */}
      <p className="log__hint" data-testid="plp-fintrennung">{t("plp.financeSeparation")}</p>
    </section>
  );
}
